import React, { useEffect } from "react";
import styles from "./HomeUpdate.module.css";
import { Header, PostRadioButton, Footer } from "@/components";
import { homeUpdateState, defaultHomeUpdateState } from "@pages/state";
import { useAtom } from "jotai";
import { useConfirm } from "@/components";
import Axios from "@/Axios";
import { useNavigate } from "react-router-dom";
import {
  useLoadReset,
  useDebouncedCallback,
  useDimLoading,
  useDimLoadComplete,
} from "@/hooks";

export default function HomeUpdate() {
  const dimLoading = useDimLoading();
  const dimLoadComplete = useDimLoadComplete();
  const loadReset = useLoadReset();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [state, setState] = useAtom(homeUpdateState);
  const resetState = () => {
    setState({ ...defaultHomeUpdateState });
    loadReset();
  };

  const checked = (name) => {
    setState((s) => ({ ...s, [name]: !s[name] }));
  };
  const radioOnOff = (name, onOff) => {
    setState((s) => ({
      ...s,
      isViewFlags: { ...s.isViewFlags, [name]: onOff },
    }));
  };
  const restructure = () => {
    const newStruct = {};
    if (state.checkedHome && state.homeMenuNm) {
      newStruct.homeMenuNm = state.homeMenuNm;
      newStruct.isViewFlags = { home: state.isViewFlags.home };
    }
    if (state.checkedReels && state.reelsMenuNm) {
      newStruct.reelsMenuNm = state.reelsMenuNm;
      newStruct.isViewFlags = {
        ...newStruct.isViewFlags,
        reels: state.isViewFlags.reels,
      };
    }
    if (state.checkedProducts && state.productsMenuNm) {
      newStruct.productsMenuNm = state.productsMenuNm;
      newStruct.isViewFlags = {
        ...newStruct.isViewFlags,
        products: state.isViewFlags.products,
      };
    }
    if (state.checkedDetail && state.detailMenuNm) {
      newStruct.detailMenuNm = state.detailMenuNm;
      newStruct.isViewFlags = {
        ...newStruct.isViewFlags,
        detail: state.isViewFlags.detail,
      };
    }
    return newStruct;
  };
  const submit = useDebouncedCallback(() => {
    confirm("홈 메뉴 수정을 진행하시겠습니까?", () => {
      const data = restructure();
      if (Object.keys(data).length === 0) {
        confirm("변경된 내용이 없습니다. 정보를 확인해주세요.");
        return;
      }

      dimLoading();
      Axios.post("/api/home/update", data).then((res) => {
        if (res.data?.errMsg) {
          dimLoadComplete(() => {
            confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
          });
          return;
        }

        dimLoadComplete(() => {
          confirm(
            "홈 메뉴 수정이 완료되었습니다.\n페이지를 이동하시겠습니까?",
            () => {
              resetState();
              navigate("/", { replace: true });
            }
          );
        });
      });
    });
  });
  const focusBlur = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  useEffect(() => {
    return resetState;
  }, []);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        <div className={styles.contentContainer}>
          <Header title={"홈 메뉴 수정"} isShare={false} />
          <div className={styles.homeMenuTitle}>
            <span className={styles.homeMenuStructure}>홈 메뉴 구성</span>
          </div>
          <div className={styles.homeScreenContainer}>
            <div className={styles.homeScreenArea}>
              <div className={styles.homeScreenTitle}>
                <span className={styles.homeScreen}>홈 화면</span>
              </div>
              <div className={styles.homeScreenInput}>
                <input
                  className={styles.inputPlaceholder}
                  placeholder={"변경할 메뉴 명을 입력해주세요"}
                  onKeyUp={(e) => {
                    focusBlur(e);
                    setState((s) => ({ ...s, homeMenuNm: e.target.value }));
                  }}
                  disabled={!state.checkedHome}
                />
                <div
                  className={
                    state.checkedHome
                      ? styles.checkBoxContainer
                      : `${styles.checkBoxContainer} ${styles.checkBoxContainerDisabled}`
                  }
                  onClick={() => {
                    checked("checkedHome");
                  }}
                >
                  <div className={styles.checkBoxArea}>
                    {state.checkedHome && (
                      <div className={styles.checkedIcon} />
                    )}
                    {!state.checkedHome && <div className={styles.checkIcon} />}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <PostRadioButton
            postClick={() => {
              radioOnOff("home", true);
            }}
            notPostClick={() => {
              radioOnOff("home", false);
            }}
            disabled={!state.checkedHome}
          />
          <div className={styles.releaseCollectionContainer}>
            <div className={styles.releaseCollectionArea}>
              <div className={styles.releaseCollectionTitle}>
                <span className={styles.releaseCollection}>릴스 모아보기</span>
              </div>
              <div className={styles.releaseCollectionInput}>
                <input
                  className={styles.inputPlaceholder}
                  placeholder={"변경할 메뉴 명을 입력해주세요"}
                  onKeyUp={(e) => {
                    focusBlur(e);
                    setState((s) => ({ ...s, reelsMenuNm: e.target.value }));
                  }}
                  disabled={!state.checkedReels}
                />
                <div
                  className={
                    state.checkedReels
                      ? styles.checkBoxContainer
                      : `${styles.checkBoxContainer} ${styles.checkBoxContainerDisabled}`
                  }
                  onClick={() => {
                    checked("checkedReels");
                  }}
                >
                  <div className={styles.checkBoxArea}>
                    {state.checkedReels && (
                      <div className={styles.checkedIcon} />
                    )}
                    {!state.checkedReels && (
                      <div className={styles.checkIcon} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <PostRadioButton
            postClick={() => {
              radioOnOff("reels", true);
            }}
            notPostClick={() => {
              radioOnOff("reels", false);
            }}
            disabled={!state.checkedReels}
          />
          <div className={styles.productIntroContainer}>
            <div className={styles.productIntroArea}>
              <div className={styles.productIntroTitle}>
                <span className={styles.productIntro}>상품 소개</span>
              </div>
              <div className={styles.productIntroInput}>
                <input
                  className={styles.inputPlaceholder}
                  placeholder={"변경할 메뉴 명을 입력해주세요"}
                  onKeyUp={(e) => {
                    focusBlur(e);
                    setState((s) => ({ ...s, productsMenuNm: e.target.value }));
                  }}
                  disabled={!state.checkedProducts}
                />
                <div
                  className={
                    state.checkedProducts
                      ? styles.checkBoxContainer
                      : `${styles.checkBoxContainer} ${styles.checkBoxContainerDisabled}`
                  }
                  onClick={() => {
                    checked("checkedProducts");
                  }}
                >
                  <div className={styles.checkBoxArea}>
                    {state.checkedProducts && (
                      <div className={styles.checkedIcon} />
                    )}
                    {!state.checkedProducts && (
                      <div className={styles.checkIcon} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <PostRadioButton
            postClick={() => {
              radioOnOff("products", true);
            }}
            notPostClick={() => {
              radioOnOff("products", false);
            }}
            disabled={!state.checkedProducts}
          />
          <div className={styles.advancedInfoContainer}>
            <div className={styles.advancedInfoArea}>
              <div className={styles.advancedInfoTitle}>
                <span className={styles.advancedInfoText}>심화 정보</span>
              </div>
              <div className={styles.advancedInfoInput}>
                <input
                  className={styles.inputPlaceholder}
                  placeholder={"변경할 메뉴 명을 입력해주세요"}
                  onKeyUp={(e) => {
                    focusBlur(e);
                    setState((s) => ({ ...s, detailMenuNm: e.target.value }));
                  }}
                  disabled={!state.checkedDetail}
                />
                <div
                  className={
                    state.checkedDetail
                      ? styles.checkBoxContainer
                      : `${styles.checkBoxContainer} ${styles.checkBoxContainerDisabled}`
                  }
                  onClick={() => {
                    checked("checkedDetail");
                  }}
                >
                  <div className={styles.checkBoxArea}>
                    {state.checkedDetail && (
                      <div className={styles.checkedIcon} />
                    )}
                    {!state.checkedDetail && (
                      <div className={styles.checkIcon} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <PostRadioButton
            postClick={() => {
              radioOnOff("detail", true);
            }}
            notPostClick={() => {
              radioOnOff("detail", false);
            }}
            disabled={!state.checkedDetail}
          />
        </div>
        <div className={styles.editButtonArea}>
          <div className={styles.submitButton}>
            <div className={styles.buttonContainer} onClick={submit}>
              <span className={styles.register}>수정</span>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
